import api from '@/services/api'
import type {
  ChecklistReservaVersao,
  DecisaoItemChecklistResponse,
  ItemBloqueanteChecklist,
  ReaproveitarBasicosResponse,
  RecusarItemRequest,
  SolicitacaoDocumentoReserva,
  SolicitarDocumentoRequest,
  ValidarItemRequest,
} from '@/types/checklist'
import type { ChecklistReserva } from '@/types/estruturacao'

/**
 * Checklist versionado de UMA reserva (AN-PP3 §6.2; contrato real em `CONTRATO-PP3-O2.md`):
 * `ChecklistReservaController` + o `GET /{id}/checklist` do `EstruturacaoConsultorController`.
 *
 * Escopo é do backend: consultor só a própria carteira do próprio tenant (fora → **404**, antes
 * 400); escrita (validar, recusar, solicitar, cancelar, atualizar versão) só o back-office —
 * CONSULTOR, mesmo com OPERADOR residual, recebe 403. A tela não calcula nada: status, validade
 * (`validoAte = dataEmissao + validadeDias`), percentual e contadores vêm prontos. Os controllers
 * devolvem o DTO direto (o interceptor de `api` já entrega o corpo).
 */

const base = (id: string) => `/api/v1/oportunidades/${id}/checklist`
const item = (id: string, chave: string) => `${base(id)}/itens/${encodeURIComponent(chave)}`

export const checklistReservaService = {
  /** Checklist da reserva pela versão PRESA (não a mais recente), com status por item. */
  obter: async (id: string): Promise<ChecklistReserva> =>
    (await api.get(base(id))) as unknown as ChecklistReserva,

  /** Versão presa, vigente do produto e histórico de vínculos. */
  versao: async (id: string): Promise<ChecklistReservaVersao> =>
    (await api.get(`${base(id)}/versao`)) as unknown as ChecklistReservaVersao,

  /** Revincula à versão vigente (back-office; só RASCUNHO/COM_PENDENCIA). */
  atualizarVersao: async (id: string, motivo: string): Promise<ChecklistReservaVersao> =>
    (await api.post(`${base(id)}/atualizar-versao`, { motivo })) as unknown as ChecklistReservaVersao,

  /** Valida o conjunto de arquivos que o Operador viu (back-office). */
  validar: async (id: string, chave: string, request: ValidarItemRequest): Promise<DecisaoItemChecklistResponse> =>
    (await api.post(`${item(id, chave)}/validar`, request)) as unknown as DecisaoItemChecklistResponse,

  /** Recusa (ou invalida) o item — motivo obrigatório (back-office). */
  recusar: async (id: string, chave: string, request: RecusarItemRequest): Promise<DecisaoItemChecklistResponse> =>
    (await api.post(`${item(id, chave)}/recusar`, request)) as unknown as DecisaoItemChecklistResponse,

  /** Histórico de decisões, mais recente primeiro. Consultor: sem `autorNome`. */
  decisoes: async (id: string, chave: string): Promise<DecisaoItemChecklistResponse[]> =>
    ((await api.get(`${item(id, chave)}/decisoes`)) as unknown as DecisaoItemChecklistResponse[] | null) ?? [],

  /** Solicitações da Curadoria da reserva, inclusive canceladas. */
  solicitacoes: async (id: string): Promise<SolicitacaoDocumentoReserva[]> =>
    ((await api.get(`${base(id)}/solicitacoes`)) as unknown as SolicitacaoDocumentoReserva[] | null) ?? [],

  /** Cria o item `sol-N` (back-office) — 201. */
  solicitar: async (id: string, request: SolicitarDocumentoRequest): Promise<SolicitacaoDocumentoReserva> =>
    (await api.post(`${base(id)}/solicitacoes`, request)) as unknown as SolicitacaoDocumentoReserva,

  /**
   * Cancelamento lógico (back-office); os arquivos já enviados ficam na reserva. POST com corpo — o DELETE com
   * corpo continua no backend só por compatibilidade.
   */
  cancelarSolicitacao: async (id: string, chave: string, motivo: string): Promise<SolicitacaoDocumentoReserva> =>
    (await api.post(`${base(id)}/solicitacoes/${encodeURIComponent(chave)}/cancelar`, {
      motivo,
    })) as unknown as SolicitacaoDocumentoReserva,

  /** Dispara de novo o reuso automático dos básicos da própria carteira. Sem metadado de origem. */
  reaproveitarBasicos: async (id: string): Promise<ReaproveitarBasicosResponse> =>
    (await api.post(`${base(id)}/reaproveitar-basicos`)) as unknown as ReaproveitarBasicosResponse,
}

// ─── Erros (envelope `RequisicaoFalhou` de `api`: `data` é o ApiResponse do backend) ───

export const codigoErroChecklistReserva = (e: unknown): string | undefined => {
  const data = (e as { data?: { errorCode?: unknown } } | null)?.data
  return typeof data?.errorCode === 'string' ? data.errorCode : undefined
}

/** Códigos dos bloqueios por checklist (422 com `data = [{chave, rotulo, status}]`). */
export const CODIGOS_BLOQUEIO_CHECKLIST = ['DOCUMENTOS_OBRIGATORIOS_FALTANDO', 'CHECKLIST_NAO_VALIDADO'] as const

/**
 * Itens que barraram a transição (`DOCUMENTOS_OBRIGATORIOS_FALTANDO` no envio e na volta da
 * pendência; `CHECKLIST_NAO_VALIDADO` no encaminhamento). Vazio em qualquer outro erro.
 */
export const itensBloqueantesDoErro = (e: unknown): ItemBloqueanteChecklist[] => {
  const codigo = codigoErroChecklistReserva(e)
  if (!codigo || !(CODIGOS_BLOQUEIO_CHECKLIST as readonly string[]).includes(codigo)) return []
  const lista = (e as { data?: { data?: unknown } } | null)?.data?.data
  if (!Array.isArray(lista)) return []
  return lista.filter(
    (x): x is ItemBloqueanteChecklist =>
      !!x && typeof x === 'object' && typeof (x as ItemBloqueanteChecklist).rotulo === 'string',
  )
}

/** A mensagem do backend como veio (409/422 já são frases para o usuário); senão o padrão. */
export const mensagemErroChecklistReserva = (e: unknown, padrao: string): string => {
  const status = (e as { status?: number } | null)?.status
  const msg = (e as { message?: unknown } | null)?.message
  if (status === 403) return 'Esta ação é do back-office da Originei.'
  if (status === 404 && !(typeof msg === 'string' && msg.trim())) return 'Reserva fora do seu escopo ou inexistente.'
  return typeof msg === 'string' && msg.trim() ? msg : padrao
}

/** 404 (fora do escopo — antes vinha 400) ou 403: a tela mostra "sem permissão". */
export const ehForaDoEscopo = (e: unknown): boolean => {
  const status = (e as { status?: number } | null)?.status
  return status === 404 || status === 403
}
